import React, { useContext } from "react";
import { map } from "lodash";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  FEATURE_FLAG_NAME,
  OBJECT_TYPE,
  SUBMISSION_CATEGORY,
  SUBMISSION_TYPE_MODULE,
} from "common/constants";
import { Attachments, isAttachableToMap } from "common/utils/attachments";
import { getObjectDisplay } from "common-client/utils/customObject";
import { useGetResidentCustomObjectInfoQuery } from "../../../generated/graphql";
import useNavigationTab from "../../../hooks/useNavigationTabs";
import { useIsScreenSize, useWindowSize } from "../../../hooks/useWindowSize";
import { spacing } from "../../../stitches.config";
import { setTitle } from "../../../utils/title";
import { AuthContext } from "../../Authorization/AuthContext";
import { ObjectCarousel } from "../../Common/Carousel/Carousel";
import { CreateSubmissionButton } from "../../Common/CreateSubmissionButton";
import { createSubmissionRedirect } from "../../Common/CreateSubmissionButton/utils";
import Icon, { ICON_COLORS, Icons } from "../../Common/Icons";
import TabGroup from "../../Common/TabGroup";
import { LayerContextProvider } from "../../Maps/layers";
import MapPreview from "../../Maps/Preview";
import { Bounds, calculateViewport } from "../../Maps/utils/viewportHook";
import AccessDeniedPage, { ACCESS_DENIED_RESOURCE } from "../AccessDeniedPage";
import Layout from "../Layout";
import { PageSubheader } from "../PageSubheader";
import { PrintButton } from "../PrintButton";
import {
  AddressInfoContainer,
  CarouselContainer,
  LoadingContainer,
  MapboxAttributionContainer,
  MapboxIconContainer,
  PropertyDetailsContainer,
  TabGroupContainer,
} from "../Property/__styles__/PropertyPage";
import { Files } from "./Files";
import { Overview } from "./Overview";
import { Records } from "./Records";

interface URLParams {
  customMapId: string;
  geometryId: string;
}

interface Props extends RouteComponentProps<URLParams> {
  print: typeof window.print;
}

enum TAB_NAMES {
  OVERVIEW = "overview",
  RECORDS = "records",
  FILES = "documents",
}

const CustomObjectPage = ({ match, print = window.print }: Props) => {
  const { geometryId } = match.params;
  const { account } = useContext(AuthContext);
  const history = useHistory();
  const { t } = useTranslation();

  const { data, loading } = useGetResidentCustomObjectInfoQuery({
    variables: { geometryId, accountId: account!.id },
    fetchPolicy: "network-only",
  });

  const customMapId = data?.customMapGeometry?.customMap.id;
  const defaultVisibleCategories =
    account?.submissionTypes
      .filter(
        submissionType =>
          !submissionType.hiddenFromPublicByDefault &&
          customMapId &&
          isAttachableToMap({
            attachments: submissionType.attachments as Attachments,
            customMapId,
          }),
      )
      .map(st => st.category) ?? [];
  const existingSubmissionCategories = (
    data?.customMapGeometry?.submissions ?? []
  ).map(s => s.submissionTypeVersion.submissionType.category);
  const categories = new Set<SUBMISSION_CATEGORY>([
    ...defaultVisibleCategories,
    ...existingSubmissionCategories,
  ]);

  const tabs: Array<{ value: TAB_NAMES; label: string }> = [
    { value: TAB_NAMES.OVERVIEW, label: t("tab-overview") },
    ...(categories.size > 0 ?
      [{ value: TAB_NAMES.RECORDS, label: t("property-records") }]
    : []),
    { value: TAB_NAMES.FILES, label: t("common-files") },
  ];

  const [tab, setTab] = useNavigationTab({
    defaultTab: TAB_NAMES.OVERVIEW,
    allowedTabs: map(tabs, "value"),
  });

  const { width } = useWindowSize();
  const isSmallScreen = useIsScreenSize("mobile", "tablet");
  const mapHeight = isSmallScreen ? width * 0.52 : 400;

  if (loading) {
    return (
      <LoadingContainer>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </LoadingContainer>
    );
  }

  if (!data || !data.customMapGeometry || !data.account || !customMapId) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.CUSTOM_OBJECT} />;
  }

  const { label, interiorPoint, coverImageDocumentUploadId, additionalFields } =
    data.customMapGeometry;
  const customMapName = data.customMapGeometry.customMap.name;
  const config = data.customMapGeometry.customMap.config;

  const latitude = interiorPoint?.[1];
  const longitude = interiorPoint?.[0];

  const bounds = data.customMapGeometry.bounds as Bounds | null;
  const zoom =
    bounds ?
      calculateViewport({
        viewport: { bounds },
        infoPanelOpen: false,
        isDesktop: !isSmallScreen,
      }).viewport.zoom
    : undefined;

  const customMap = account!.customMaps.find(m => m.id === customMapId);
  const customMapsLayer =
    customMap ?
      { maps: [customMap], value: { customMapId, geometryId } }
    : undefined;

  setTitle(label);

  return (
    <LayerContextProvider
      account={data.account}
      noLayerPanel={true}
      defaultState={{
        customMaps: { [customMapId]: true },
      }}
    >
      <Layout hasGutters={true}>
        <div style={{ paddingBottom: spacing.m.value }}>
          <PageSubheader
            showDivider={false}
            additionalControls={<PrintButton print={print} />}
          />
        </div>

        {latitude && longitude && (
          <MapPreview
            height={`${mapHeight}px`}
            width={"100%"}
            latitude={latitude}
            longitude={longitude}
            zoom={zoom}
            account={data.account!}
            customMaps={customMapsLayer}
          />
        )}

        <MapboxAttributionContainer>
          <MapboxIconContainer>
            <Icon icon={Icons.MAPBOX_ATTRIBUTION} />
          </MapboxIconContainer>
          <span>&copy; Mapbox, &copy; OpenStreetMap</span>
        </MapboxAttributionContainer>
        <PropertyDetailsContainer>
          <AddressInfoContainer>
            <h1>{label}</h1>
            <h2>{customMapName}</h2>
            <div style={{ marginTop: spacing.m.value }}>
              <CreateSubmissionButton
                buttonText={t("property-button-new-record")}
                hideIfNoOptions={true}
                disabled={false}
                isPublic={true}
                objectDisplay={getObjectDisplay({
                  objectType: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
                  object: { label },
                })}
                onSubmit={submissionTypeId =>
                  createSubmissionRedirect({
                    history,
                    submissionTypeId,
                    objectId: geometryId,
                    objectType: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
                  })
                }
                submissionTypeFilter={submissionType =>
                  !submissionType.modules.includes(
                    SUBMISSION_TYPE_MODULE.EC_SUBMISSION,
                  ) &&
                  isAttachableToMap({
                    attachments: submissionType.attachments as Attachments,
                    customMapId,
                  })
                }
              />
            </div>
          </AddressInfoContainer>
          <CarouselContainer>
            <ObjectCarousel
              label={label}
              objectId={geometryId}
              objectType={OBJECT_TYPE.CUSTOM_MAP_GEOMETRY}
              coverImageDocumentUploadId={coverImageDocumentUploadId ?? null}
              size="small"
            />
          </CarouselContainer>
        </PropertyDetailsContainer>

        <TabGroupContainer data-testid="tabs">
          <TabGroup currentTab={tab} setTab={setTab} tabs={tabs} />
        </TabGroupContainer>
        {tab === TAB_NAMES.OVERVIEW && (
          <Overview additionalFields={additionalFields} config={config} />
        )}
        {tab === TAB_NAMES.RECORDS && (
          <Records geometryId={geometryId} categories={categories} />
        )}
        {tab === TAB_NAMES.FILES && (
          <Files
            geometryId={geometryId}
            geometryLabel={label}
            customMapId={customMapId}
          />
        )}
      </Layout>
    </LayerContextProvider>
  );
};

const CustomObjectWrapper = (props: Props) => {
  const { isFeatureEnabled } = useContext(AuthContext);

  if (!isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_CUSTOM_OBJECTS)) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.CUSTOM_OBJECT} />;
  }

  return <CustomObjectPage {...props} />;
};

export default CustomObjectWrapper;
